import { EmoNeutral, EmoPin } from "@assets"
import { Head, router } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { useEffect, useState } from "react"

import FeedItem, {
  type FeedItem as FeedItemType,
} from "@/components/feed/feed-item"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import { eventsNewPath, eventsPath } from "@/routes"

interface EventsIndexProps {
  events: FeedItemType[]
  filters: {
    status?: string
  }
  pagination: {
    current_offset: number
    limit: number
    total: number
    has_more: boolean
  }
  can_create: boolean
  has_events_for_checkin?: boolean
}

interface EventsListProps {
  events: FeedItemType[]
  allEvents: FeedItemType[]
  title: string
  pagination: {
    has_more: boolean
    limit: number
  }
  loadingMore?: boolean
  onLoadMore?: () => void
}

function EventsList({
  events,
  allEvents,
  title,
  pagination,
  loadingMore = false,
  onLoadMore,
}: EventsListProps) {
  if (events.length === 0) {
    return (
      <div
        className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
        style={{ background: "var(--gradient-highlight)" }}
      >
        <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
          <img src={EmoNeutral} alt="Sin contenido" />
        </div>

        <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
          No se encontraron
          <br />
          eventos {title}
        </h3>
      </div>
    )
  }

  return (
    <>
      <div className="">
        {allEvents.map((event) => (
          <FeedItem key={event.id} item={event} variant="card" />
        ))}
      </div>

      {pagination.has_more && onLoadMore && (
        <div className="border-border border-t p-4 text-center">
          <Button
            variant="ghost"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="text-primary hover:bg-muted"
          >
            {loadingMore ? "Cargando..." : "Ver más eventos"}
          </Button>
        </div>
      )}
    </>
  )
}

export default function EventsIndex({
  events,
  filters,
  pagination,
  can_create,
  has_events_for_checkin,
}: EventsIndexProps) {
  const [allEvents, setAllEvents] = useState(events)
  const [loadingMore, setLoadingMore] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)
  const [currentStatusFilter, setCurrentStatusFilter] = useState(
    filters.status ?? "upcoming",
  )

  useEffect(() => {
    if (isRequesting) return

    if (events.length > 0) {
      setAllEvents((prevItems) => {
        const newStatus = filters.status ?? "upcoming"
        if (newStatus !== currentStatusFilter) {
          setCurrentStatusFilter(newStatus)
          return events
        }

        if (pagination.current_offset > 0) {
          const newItems = events.filter(
            (newItem) =>
              !prevItems.some((existingItem) => existingItem.id === newItem.id),
          )
          return [...prevItems, ...newItems]
        } else {
          return events
        }
      })
    } else if (events.length === 0 && pagination.current_offset === 0) {
      setAllEvents([])
    }
  }, [
    events,
    pagination.current_offset,
    filters.status,
    currentStatusFilter,
    isRequesting,
  ])

  const handleFilterChange = (key: string, value: string) => {
    if (isRequesting) return

    setIsRequesting(true)
    setLoadingMore(false)
    setCurrentStatusFilter(value)
    router.get(
      eventsPath(),
      {
        ...filters,
        [key]: value === "" ? undefined : value,
        offset: 0,
      },
      {
        preserveState: true,
        preserveScroll: true,
        onFinish: () => setIsRequesting(false),
      },
    )
  }

  const handleLoadMore = () => {
    if (loadingMore || !pagination.has_more || isRequesting) return

    setLoadingMore(true)
    setIsRequesting(true)
    const nextOffset = pagination.current_offset + pagination.limit

    router.get(
      eventsPath(),
      {
        ...filters,
        offset: nextOffset,
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["events", "pagination"],
        onFinish: () => {
          setLoadingMore(false)
          setIsRequesting(false)
        },
      },
    )
  }

  const currentStatus =
    filters.status && filters.status !== "" ? filters.status : "upcoming"

  return (
    <AppLayout>
      <Head title="Eventos" />
      <div className="bg-background min-h-screen">
        <div className="mx-auto max-w-6xl space-y-3 md:p-6">
          <div className="flex flex-col">
            <div className="flex items-center justify-center gap-4">
              <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
                <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
                  <img
                    src={EmoPin}
                    alt="Pin"
                    className="h-full w-full object-contain"
                  />
                </span>
                Eventos
              </h1>
              {can_create && (
                <Button
                  variant="default"
                  onClick={() => router.visit(eventsNewPath())}
                  className="flex flex-shrink-0 items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Crear evento</span>
                </Button>
              )}
            </div>
            <p className="mx-10 mt-[8px] text-center text-[13px] font-normal tracking-[0.2px] text-balance">
              Descubre y participa en eventos de la comunidad argentina
            </p>
          </div>
          <Tabs
            value={currentStatus}
            onValueChange={(v) => handleFilterChange("status", v)}
            className="space-y-6"
          >
            <div className="flex flex-wrap justify-center gap-2">
              <TabsList className="mx-1 h-auto flex-wrap justify-center gap-1">
                <TabsTrigger value="upcoming" disabled={isRequesting}>
                  Próximos
                </TabsTrigger>
                <TabsTrigger value="free" disabled={isRequesting}>
                  Gratis
                </TabsTrigger>
                <TabsTrigger value="featured" disabled={isRequesting}>
                  Destacados
                </TabsTrigger>
                {has_events_for_checkin && (
                  <TabsTrigger value="my_events" disabled={isRequesting}>
                    Check-in
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            <TabsContent value="upcoming" className="space-y-6">
              <EventsList
                events={events}
                allEvents={allEvents}
                title="próximos"
                pagination={pagination}
                onLoadMore={handleLoadMore}
              />
            </TabsContent>

            <TabsContent value="free" className="space-y-6">
              <EventsList
                events={events}
                allEvents={allEvents}
                title="gratis"
                pagination={pagination}
                onLoadMore={handleLoadMore}
              />
            </TabsContent>

            <TabsContent value="featured" className="space-y-6">
              <EventsList
                events={events}
                allEvents={allEvents}
                title="destacados"
                pagination={pagination}
                onLoadMore={handleLoadMore}
              />
            </TabsContent>

            {has_events_for_checkin && (
              <TabsContent value="my_events" className="space-y-6">
                <EventsList
                  events={events}
                  allEvents={allEvents}
                  title="eventos para check-in"
                  pagination={pagination}
                  onLoadMore={handleLoadMore}
                />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </AppLayout>
  )
}
